import useSWR from "swr";
import { PLATFORM_BASE_URL } from "../env";

import { useRouter } from "next/navigation";
import type { Profile } from "../models";
import { isJWTExpired } from "./use-stytch-jwt";

interface UseProfileData {
  profile: Profile | null;
  errorCode?: string;
  status: number;
}

interface UseProfile {
  data: UseProfileData | null | undefined;
  error: Error | undefined;
  isLoading: boolean;
}

export const profileURL = `${PLATFORM_BASE_URL}/user/profile`;

export const useProfile = (sessionJwt?: string): UseProfile => {
  const url = profileURL;
  const isReadyJwt = sessionJwt && !isJWTExpired(sessionJwt);

  const router = useRouter();
  const fetcher = async (): Promise<UseProfileData | null> => {
    if (!sessionJwt) {
      throw new Error("No session token found");
    }

    // pre-check if the token is expired
    if (isJWTExpired(sessionJwt)) {
      return {
        profile: null,
        errorCode: "invalid_or_expired_jwt",
        status: 401,
      };
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${sessionJwt}`,
        "content-type": "application/json",
      },
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401) {
        return {
          profile: null,
          errorCode: data.error.code,
          status: 401,
        };
      }
      // send the user to signup again if the user is not found
      // this could happen if orion user is deleted, but stytch user is not
      if (response.status === 404 && data.error.code === "user_not_found") {
        router.push("/authenticate/signup");
        return null;
      }
      throw new Error(data.error);
    }

    return { profile: data, status: 200 };
  };

  return useSWR(isReadyJwt ? url : null, fetcher, {
    revalidateOnFocus: false,
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    onErrorRetry: (error: any, _key, _config, _revalidate, { retryCount }) => {
      if (error.status >= 400 && error.status < 500) {
        return;
      }
      if (retryCount >= 2) {
        return;
      }
    },
  });
};
